import APIService from "./APIService";

/**
 * API service for image upload operations.
 * Uses fetch API for S3 direct uploads (not axios).
 */
class ImageUploadAPI {
  /**
   * Generate presigned URL for direct S3 upload
   * @param {string} fileName - Name of the file to upload
   * @param {string} fileType - MIME type of the file
   * @param {number} fileSize - Size of the file in bytes
   * @returns {Promise<Object>} Presigned URL response
   */
  async generatePresignedUrl(fileName, fileType, fileSize) {
    try {
      const response = await APIService.post(
        "/api/v1/scam/detector/image/upload/presigned-url",
        {
          fileName,
          fileType,
          fileSize,
        },
      );
      return response;
    } catch (error) {
      console.error("Error generating presigned URL:", error);
      throw error;
    }
  }

  /**
   * Upload image directly to S3 using presigned URL.
   * Uses native fetch API instead of axios.
   * @param {string} presignedUrl - Presigned URL from generatePresignedUrl
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Promise<void>}
   */
  async uploadToS3(presignedUrl, file, onProgress) {
    try {
      // For progress tracking, we need to use XMLHttpRequest
      if (onProgress) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percentCompleted = Math.round(
                (event.loaded * 100) / event.total,
              );
              onProgress(percentCompleted);
            }
          });

          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          });

          xhr.addEventListener("error", () => {
            reject(new Error("Upload failed"));
          });

          xhr.open("PUT", presignedUrl);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.send(file);
        });
      } else {
        // Simple fetch without progress
        const response = await fetch(presignedUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }
      }
    } catch (error) {
      console.error("Error uploading to S3:", error);
      throw error;
    }
  }

  /**
   * Complete upload and trigger processing
   * @param {string} uploadId - Upload ID from presigned URL response
   * @param {string} s3Key - S3 key from presigned URL response
   * @param {string} fileName - Original file name
   * @param {string} fileType - File MIME type
   * @param {number} fileSize - File size in bytes
   * @param {string} userContext - Optional user-provided context
   * @returns {Promise<Object>} Completion response
   */
  async completeUpload(
    uploadId,
    s3Key,
    fileName,
    fileType,
    fileSize,
    userContext,
  ) {
    try {
      const body = {
        uploadId,
        s3Key,
        fileName,
        fileType,
        fileSize,
      };
      if (userContext && userContext.trim()) {
        body.userContext = userContext.trim();
      }
      const response = await APIService.post(
        "/api/v1/scam/detector/image/upload/complete",
        body,
      );
      return response;
    } catch (error) {
      console.error("Error completing upload:", error);
      throw error;
    }
  }

  /**
   * Full upload workflow: generate URL, upload to S3, complete upload
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback (optional)
   * @param {string} userContext - Optional user-provided context
   * @returns {Promise<Object>} Upload result with uploadId
   */
  async uploadImage(file, onProgress, userContext) {
    try {
      // Step 1: Generate presigned URL
      if (onProgress) onProgress(0, "Preparing upload...");
      const urlResponse = await this.generatePresignedUrl(
        file.name,
        file.type,
        file.size,
      );

      // Step 2: Upload to S3
      if (onProgress) onProgress(10, "Uploading image...");
      await this.uploadToS3(urlResponse.uploadUrl, file, (progress) => {
        // Map S3 upload progress to 10-90%
        const adjustedProgress = 10 + progress * 0.8;
        if (onProgress) onProgress(adjustedProgress, "Uploading image...");
      });

      // Step 3: Complete upload
      if (onProgress) onProgress(90, "Finalizing upload...");
      const completeResponse = await this.completeUpload(
        urlResponse.uploadId,
        urlResponse.s3Key,
        file.name,
        file.type,
        file.size,
        userContext,
      );

      if (onProgress) onProgress(100, "Upload complete!");
      return completeResponse;
    } catch (error) {
      console.error("Error in upload workflow:", error);
      throw error;
    }
  }
}

export default new ImageUploadAPI();
